import type { EditorCommands, TiptapEditorPlugin } from 'ricos-types';
import { createShortcut } from 'ricos-types';
import { getCurrentTextAlignmentIcon } from 'wix-rich-content-toolbars-modals/libs/getCurrentTextAlignmentIcon';
import { RESOLVERS_IDS } from 'wix-rich-content-toolbars-v3/libs/resolvers-ids';

import AlignmentPanelComponent from './AlignmentPanelComponent';
import {
  TEXT_ALIGNMENT_BUTTON_DATA_HOOK,
  TEXT_ALIGNMENT_CENTER_BUTTON_DATA_HOOK,
  TEXT_ALIGNMENT_JUSTIFY_BUTTON_DATA_HOOK,
  TEXT_ALIGNMENT_LEFT_BUTTON_DATA_HOOK,
  TEXT_ALIGNMENT_RIGHT_BUTTON_DATA_HOOK,
} from './data-hooks';
import { textAlign } from './extension';
import { AlignJustifyIcon, AlignLeftIcon, AlignRightIcon, AlignTextCenterIcon } from './icons';

const FORMATTING_ALIGNMENT_MODAL_ID = 'TextAlignment.modal';

const getAlignmentIcon = (editorCommands: EditorCommands): ((props) => JSX.Element) => {
  return getCurrentTextAlignmentIcon(editorCommands);
};

export const pluginTextAlignment: TiptapEditorPlugin = {
  type: 'TEXT_ALIGNMENT',
  config: {},
  tiptapExtensions: [textAlign],
  shortcuts: [
    createShortcut({
      name: 'TextAlignment.LEFT',
      description: 'ShortcutsSheet_Formatting_AlignLeft',
      keys: { macOs: 'Meta+Shift+L', windows: 'Ctrl+Shift+L' },
      command({ editorCommands }) {
        editorCommands.setTextAlignment('left');
      },
      group: 'ShortcutsSheet_Section_Formatting',
      enabled: true,
    }),
    createShortcut({
      name: 'TextAlignment.CENTER',
      description: 'ShortcutsSheet_Formatting_AlignCenter',
      keys: { macOs: 'Meta+Shift+E', windows: 'Ctrl+Shift+E' },
      command({ editorCommands }) {
        editorCommands.setTextAlignment('center');
      },
      group: 'ShortcutsSheet_Section_Formatting',
      enabled: true,
    }),
    createShortcut({
      name: 'TextAlignment.RIGHT',
      description: 'ShortcutsSheet_Formatting_AlignRight',
      keys: { macOs: 'Meta+Shift+R', windows: 'Ctrl+Shift+R' },
      command({ editorCommands }) {
        editorCommands.setTextAlignment('right');
      },
      group: 'ShortcutsSheet_Section_Formatting',
      enabled: true,
    }),
    createShortcut({
      name: 'TextAlignment.JUSTIFY',
      description: 'ShortcutsSheet_Formatting_Justify',
      keys: { macOs: 'Meta+Shift+J', windows: 'Ctrl+Shift+J' },
      command({ editorCommands }) {
        editorCommands.setTextAlignment('justify');
      },
      group: 'ShortcutsSheet_Section_Formatting',
      enabled: true,
    }),
  ],
  textButtons: [
    {
      id: 'TextAlignment.ALIGNMENT',
      type: 'modal',
      presentation: {
        dataHook: TEXT_ALIGNMENT_BUTTON_DATA_HOOK,
        tooltip: 'AlignTextDropdownButton_Tooltip',
        getIcon: getAlignmentIcon,
      },
      attributes: {
        visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
        selectedAlignment: RESOLVERS_IDS.GET_ALIGNMENT_IN_SELECTION,
        disabled: RESOLVERS_IDS.IS_NODE_SELECTED,
      },
      modal: {
        id: FORMATTING_ALIGNMENT_MODAL_ID,
        Component: AlignmentPanelComponent,
      },
    },
    {
      id: 'TextAlignment.LEFT',
      type: 'toggle',
      presentation: {
        dataHook: TEXT_ALIGNMENT_LEFT_BUTTON_DATA_HOOK,
        tooltip: 'AlignTextLeftButton_Tooltip',
        icon: AlignLeftIcon,
      },
      attributes: {
        visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
        active: RESOLVERS_IDS.IS_TEXT_ALIGN_LEFT,
        disabled: RESOLVERS_IDS.IS_NODE_SELECTED,
      },
      command:
        ({ editorCommands }) =>
        () => {
          editorCommands.setTextAlignment('left');
          return true;
        },
    },
    {
      id: 'TextAlignment.CENTER',
      type: 'toggle',
      presentation: {
        dataHook: TEXT_ALIGNMENT_CENTER_BUTTON_DATA_HOOK,
        tooltip: 'AlignTextCenterButton_Tooltip',
        icon: AlignTextCenterIcon,
      },
      attributes: {
        visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
        active: RESOLVERS_IDS.IS_TEXT_ALIGN_CENTER,
        disabled: RESOLVERS_IDS.IS_NODE_SELECTED,
      },
      command:
        ({ editorCommands }) =>
        () => {
          editorCommands.setTextAlignment('center');
          return true;
        },
    },
    {
      id: 'TextAlignment.RIGHT',
      type: 'toggle',
      presentation: {
        dataHook: TEXT_ALIGNMENT_RIGHT_BUTTON_DATA_HOOK,
        tooltip: 'AlignTextRightButton_Tooltip',
        icon: AlignRightIcon,
      },
      attributes: {
        visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
        active: RESOLVERS_IDS.IS_TEXT_ALIGN_RIGHT,
        disabled: RESOLVERS_IDS.IS_NODE_SELECTED,
      },
      command:
        ({ editorCommands }) =>
        () => {
          editorCommands.setTextAlignment('right');
          return true;
        },
    },
    {
      id: 'TextAlignment.JUSTIFY',
      type: 'toggle',
      presentation: {
        dataHook: TEXT_ALIGNMENT_JUSTIFY_BUTTON_DATA_HOOK,
        tooltip: 'AlignTextJustifyButton_Tooltip',
        icon: AlignJustifyIcon,
      },
      attributes: {
        visible: RESOLVERS_IDS.ALWAYS_VISIBLE,
        active: RESOLVERS_IDS.IS_TEXT_ALIGN_JUSTIFY,
        disabled: RESOLVERS_IDS.IS_NODE_SELECTED,
      },
      command:
        ({ editorCommands }) =>
        () => {
          editorCommands.setTextAlignment('justify');
          return true;
        },
    },
  ],
};
